// AI Agent Studio API client (async job pattern).
//
// Endpoints:
//   POST /agentstudio/generate         → 202 + { jobId, status, submittedAt }
//   GET  /agentstudio/jobs/{jobId}     → { jobId, status, result?, error?, ... }
//
// Usage from a React component:
//   const { jobId } = await submitAgentJob(body, getApiToken);
//   // ...poll every ~2s until status is SUCCEEDED or FAILED
//   const row = await pollAgentJob(jobId, getApiToken);
//   if (row.status === 'SUCCEEDED') doSomethingWith(row.result.json);

const API_BASE = process.env.REACT_APP_AGENT_STUDIO_API_BASE_URL || '';

export function isAgentStudioBackendConfigured() {
  return Boolean(API_BASE);
}

async function authHeaders(tokenProvider) {
  const headers = { 'Content-Type': 'application/json' };
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.warn('agentstudio: token acquisition failed', err);
    }
  }
  return headers;
}

async function parseOrThrow(res, fallbackPrefix) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { code: 'INVALID_RESPONSE', message: text };
  }
  if (!res.ok) {
    const err = new Error(data?.message || `${fallbackPrefix} failed (HTTP ${res.status})`);
    err.code = data?.code;
    err.context = data?.context;
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Submit a generate request. Returns immediately with { jobId, status:'PENDING' }.
 * The actual Bedrock generation runs asynchronously on the Lambda; poll with
 * pollAgentJob(jobId) until status is SUCCEEDED or FAILED.
 *
 * body shape: see lambda/agentstudio/agent-template/index.js header.
 */
export async function submitAgentJob(body, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return parseOrThrow(res, 'Submit');
}

/**
 * Read the current state of a job. Returns:
 *   {
 *     jobId, status,             // PENDING | RUNNING | SUCCEEDED | FAILED
 *     createdAt, updatedAt,
 *     diagramAttached,
 *     result?: { json, model, channel, generatedAt, durationMs, appliedStandards, diagramUsed },
 *     error?:  { code, message, context? }
 *   }
 */
export async function pollAgentJob(jobId, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  if (!jobId) throw new Error('jobId is required.');
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/jobs/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    headers
  });
  return parseOrThrow(res, 'Poll');
}

// ---------------------------------------------------------------------------
// Full Build endpoints (PDF → multi-bot + WxCC flow). Mirrors submit/poll
// pattern; result row carries { bots: [...], flow: {...} } on success.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// KB-mode submission ("From KB" tab) — same /agentstudio/generate endpoint
// with a `mode: "from-kb"` discriminator on the body. Result row comes back
// through pollAgentJob exactly like the form-driven path.
// ---------------------------------------------------------------------------

/**
 * Submit a KB-driven generate request.
 *
 * body shape:
 *   {
 *     platform: "wxcc",
 *     agentType: "autonomous" | "scripted",
 *     channel: "voice" | "digital",
 *     name: string,
 *     persona?: string,
 *     notes?: string,
 *     postImportNotes?: string,
 *     mode: "from-kb",
 *     kbArtifacts: [{ id, label, kind: "pdf" | "text", body, mediaType }],
 *     kbInlineText?: string
 *   }
 *
 * Returns { jobId, status:'PENDING', submittedAt } and the worker runs Bedrock
 * asynchronously. Poll with pollAgentJob until status is SUCCEEDED / FAILED.
 */
export async function submitKbAgentJob(body, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...body, mode: 'from-kb' })
  });
  return parseOrThrow(res, 'Submit From-KB');
}

/**
 * Submit a Full Build job — multi-page PDF + tenant orgId + page roster.
 * Returns { jobId, status, submittedAt, kind: 'full-build' } immediately.
 */
export async function submitFullBuildJob(body, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/full-build`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return parseOrThrow(res, 'Submit Full Build');
}

/**
 * D2 fail-path: submit a retry that regenerates only the flow (reusing prior
 * job's bots + tenant template), feeding Studio import errors back as
 * priorJudgeFindings for the DSL generator.
 *
 * body: { priorJobId: string, importErrors: string | Array<{code, message,
 *         activity?}>, note?: string }
 * Returns { jobId, priorJobId, status: 'PENDING', kind: 'full-build-retry' }.
 */
export async function submitFullBuildRetry(body, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/full-build/retry`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return parseOrThrow(res, 'Submit Full Build retry');
}

/**
 * List saved tenant profiles. Each profile carries { orgId, displayName,
 * defaultConnectorId, defaultQueueId, middlewareHints }. The full template
 * JSON is excluded from the list (fetch via getTenantProfile if needed).
 */
export async function listTenantProfiles(tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/tenant-profiles`, {
    method: 'GET',
    headers
  });
  return parseOrThrow(res, 'List tenant profiles');
}

/**
 * Save (create or update) a tenant profile.
 * body shape: { orgId, displayName, template, defaultConnectorId?,
 *               defaultQueueId?, middlewareHints?, middlewareBaseUrl? }
 */
export async function saveTenantProfile(body, tokenProvider) {
  if (!API_BASE) {
    throw new Error('AI Agent Studio API URL is not configured. Set REACT_APP_AGENT_STUDIO_API_BASE_URL.');
  }
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/agentstudio/tenant-profiles`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return parseOrThrow(res, 'Save tenant profile');
}
